import { defineStore } from 'pinia'
import { ref } from 'vue'
import { sessionStorage } from '@shared/helpers/storage'
import Corporate from '@shared/data/models/corporate.js'

export const useCorporateStore = defineStore(
  'corporate',
  () => {
    // State
    /** @type {import('vue').Ref<Corporate|null>} */
    const corporate = ref(null)

    // Actions
    /**
     * Resolve a corporate by key or ID. Reuse the stored corporate when
     * possible. Return null when loading fails.
     * @param {string | number | null} [identifier]
     * @returns {Promise<Corporate|null>}
     */
    async function resolveCorporate(identifier) {
      const current = corporate.value

      if (
        identifier == null ||
        current?.key === identifier ||
        current?.id === identifier
      ) {
        return current
      }

      const reference =
        typeof identifier === 'string'
          ? { key: identifier }
          : { id: identifier }

      try {
        // dynamic import: the api layer statically imports this store
        const { loadCorporate } = await import('@shared/api-endpoints')
        return await loadCorporate(reference)
      } catch {
        return null
      }
    }

    return {
      // State
      corporate,
      // Actions
      resolveCorporate,
    }
  },
  {
    persist: {
      storage: sessionStorage,
      serializer: {
        serialize: (state) => {
          const rawState = { ...state }
          if (rawState.corporate) {
            rawState.corporate = rawState.corporate._json
          }
          return JSON.stringify(rawState)
        },
        deserialize: (value) => {
          const parsed = JSON.parse(value)
          // Reconstruct Corporate (to restore non-primitive properties)
          if (parsed.corporate) {
            parsed.corporate = new Corporate(parsed.corporate)
          }
          return parsed
        },
      },
    },
  },
)

export default useCorporateStore
